import { useMemo } from 'react';
import { RIDES_SEX, RIDES_VERIFICATION_TYPE, type RidesEventView, type RidesLookupsView, type RidesPublicSettingsView, type RidesCampusView } from '@actschurch/ccms-sdk';
import { campusById, serviceTimeById, serviceTimesFor, useRidesEvents, useRidesLookups } from '@/lib/lookups';
import { formatShortDate, formatTime } from '@/lib/format';
import { findVerification, isSubmitted, useMemberMe, useMyVerifications, useRiderProfile } from '@/lib/me';
import { useRidesSettings } from '@/lib/settings';
import { partySize, useRidesWizard, type RiderSelection } from '@/store/ridesWizard';
import '@/styles/flows.css';

/** Kept for older imports: the rider flow now has a variable number of steps (see riderPlan). */
export const RIDER_STEPS = 6;

export type RiderStepKey = 'event' | 'from' | 'to' | 'service' | 'who' | 'driver' | 'id' | 'child' | 'guardian' | 'guardian-id' | 'book';

/** The Sunday-service step: the event uses campus service times and the drop-off campus has some. */
export function needsServiceStep(event: RidesEventView | undefined, drop: RidesCampusView | undefined, settings: RidesPublicSettingsView): boolean {
  if (!event?.usesCampusServiceTimes || !settings.requireServiceTimeForSundays) return false;
  return drop ? serviceTimesFor(drop).length > 0 : true;
}

/** The screens this rider will see, in order (one decision per screen). */
export function riderPlan(o: { needsService: boolean; forMinor: boolean; memberIsMinor?: boolean; idDone: boolean; guardianIdDone: boolean }): RiderStepKey[] {
  const plan: RiderStepKey[] = ['event', 'from', 'to'];
  if (o.needsService) plan.push('service');
  plan.push('who', 'driver');
  if (o.forMinor) {
    // A member who is under 18 is the child: no "child's name" screen (the API uses their own name).
    if (!o.memberIsMinor) plan.push('child');
    plan.push('guardian');
    if (!o.guardianIdDone) plan.push('guardian-id');
  } else if (!o.idDone) {
    plan.push('id');
  }
  plan.push('book');
  return plan;
}

/** Sex from the member profile ("Female" / "f"), used to skip asking again. */
export function sexFromGender(gender: string | null | undefined): number | null {
  const g = (gender ?? '').trim().toLowerCase();
  if (g.startsWith('f')) return RIDES_SEX.Female;
  if (g.startsWith('m')) return RIDES_SEX.Male;
  return null;
}

/** "Just me", "3 people", "Your child", "Your child and 1 other". */
export function whoLabel(sel: RiderSelection): string {
  const n = partySize(sel);
  if (sel.forMinor) {
    if (sel.party === 'group' && n > 1) return `Your child and ${n - 1} ${n - 1 === 1 ? 'other' : 'others'}`;
    return 'Your child';
  }
  if (sel.party === 'group') return `${n} people`;
  if (sel.party === 'solo') return 'Just me';
  return '';
}

/** Chips for the choices made before `upTo` ("Sunday Service, Sun 11 Oct", "From Glen Austin" …). */
export function riderChips(sel: RiderSelection, lookups: RidesLookupsView | undefined, event: RidesEventView | undefined, upTo: RiderStepKey): string[] {
  const order: RiderStepKey[] = ['event', 'from', 'to', 'service', 'who', 'driver'];
  const before = (k: RiderStepKey) => {
    const a = order.indexOf(k);
    const b = order.indexOf(upTo);
    return a >= 0 && (b < 0 || a < b);
  };
  const col = campusById(lookups, sel.collectionLocationId);
  const drop = campusById(lookups, sel.dropOffLocationId);
  const chips: string[] = [];
  const st = before('service') && event?.usesCampusServiceTimes && drop ? serviceTimeById(drop, sel.serviceTimeId) : undefined;
  if (before('event') && event) chips.push(`${event.name}, ${formatShortDate(event.date)}${st ? `, ${formatTime(st.startTime)}` : ''}`);
  if (before('to') && col && drop) chips.push(`${col.name} to ${drop.name}`);
  else if (before('from') && col) chips.push(`From ${col.name}`);
  if (before('who')) {
    const w = whoLabel(sel);
    if (w) chips.push(w);
  }
  return chips;
}

/** Everything the rider steps share: wizard state, lookups, the chosen event and campuses, and the step plan. */
export function useRiderFlow() {
  const { rider, setRider, resetRider } = useRidesWizard();
  const lookups = useRidesLookups();
  const events = useRidesEvents();
  const { settings } = useRidesSettings();
  const verifications = useMyVerifications();
  const profile = useRiderProfile();
  const member = useMemberMe();

  const event = events.data?.find((e) => e.eventID === rider.eventId);
  const col = campusById(lookups.data, rider.collectionLocationId);
  const drop = campusById(lookups.data, rider.dropOffLocationId);
  const memberIsMinor = profile.data?.isMinor ?? false;
  const forMinor = memberIsMinor || rider.forMinor === true;
  // No ID screen when the settings don't ask riders for an ID check, or it has been sent already.
  const idRequired = settings.requireRiderIdVerification !== false;
  const idDone = !idRequired || isSubmitted(findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GovernmentId));
  const guardianIdDone = !idRequired || isSubmitted(findVerification(verifications.data, RIDES_VERIFICATION_TYPE.GuardianId));
  const needsService = needsServiceStep(event, drop, settings);

  const plan = useMemo(() => riderPlan({ needsService, forMinor, memberIsMinor, idDone, guardianIdDone }), [needsService, forMinor, memberIsMinor, idDone, guardianIdDone]);
  // Before an event is chosen, count the Sunday service step too: the total may then go down, never up.
  const firstPlan = useMemo(() => riderPlan({ needsService: event ? needsService : true, forMinor, memberIsMinor, idDone, guardianIdDone }), [event, needsService, forMinor, memberIsMinor, idDone, guardianIdDone]);
  const stepOf = (k: RiderStepKey) => Math.max(1, plan.indexOf(k) + 1);

  return {
    rider, setRider, resetRider, lookups, events, settings, verifications, profile, member,
    event, col, drop, memberIsMinor, forMinor, idRequired, idDone, guardianIdDone, needsService,
    plan, total: plan.length, firstTotal: firstPlan.length, stepOf,
    chips: (upTo: RiderStepKey) => riderChips(rider, lookups.data, event, upTo),
  };
}

// ---------------------------------------------------------------- booked lifts (confirmation, ride day)

/** "Just you", "3 people" or "Your child, Lindiwe Sithole" for a booking. */
export function bookingWhoLabel(b: { forMinor: boolean; childName?: string | null; partySize: number }): string {
  if (b.forMinor) {
    const child = b.childName?.trim();
    const others = b.partySize > 1 ? ` and ${b.partySize - 1} ${b.partySize - 1 === 1 ? 'other' : 'others'}` : '';
    return child ? `${child}${others}` : `Your child${others}`;
  }
  return b.partySize > 1 ? `${b.partySize} people` : 'Just you';
}

/** Wizard state that finds another lift for the same trip (event, campuses, service and group). */
export function sameTripSelection(
  b: { eventID: number; collectionLocationID: number; dropOffLocationID: number; serviceTimeStart?: string | null; partySize: number; partyMaleCount: number; partyFemaleCount: number; forMinor: boolean; guardianName?: string | null; guardianRelationship?: string | null },
  lookups: RidesLookupsView | undefined,
): Partial<RiderSelection> {
  const drop = campusById(lookups, b.dropOffLocationID);
  const st = b.serviceTimeStart ? serviceTimesFor(drop).find((s) => formatTime(s.startTime) === formatTime(b.serviceTimeStart)) : undefined;
  const solo = b.partySize <= 1;
  return {
    eventId: b.eventID,
    collectionLocationId: b.collectionLocationID,
    dropOffLocationId: b.dropOffLocationID,
    serviceTimeId: st?.serviceTimeID ?? null,
    party: solo ? 'solo' : 'group',
    soloSexId: solo ? (b.partyFemaleCount > 0 ? RIDES_SEX.Female : RIDES_SEX.Male) : null,
    maleCount: b.partyMaleCount,
    femaleCount: b.partyFemaleCount,
    forMinor: b.forMinor,
    guardian: { name: b.guardianName ?? '', relationship: b.guardianRelationship ?? '', mobile: '' },
    chosenAvailabilityId: null,
  };
}
